import React, { useMemo } from 'react';
import ConstructionSowChatPanel from '../ConstructionSowChat/ConstructionSowChatPanel';
import type { ConstructionSowChatSessionView, ConstructionSowChatTurnInput, ConstructionSowNewUploadRole } from '../ConstructionSowChat/types';
import type { ServicesPwsArtifactRole, ServicesPwsChatSessionView, ServicesPwsChatTurnInput } from './types';

const SUPPORT_ROLES: ReadonlyArray<{ role: Exclude<ServicesPwsArtifactRole, 'requirements'>; label: string }> = [
  { role: 'supporting_requirements', label: 'Supporting requirements / workload data' },
  { role: 'prior_pws', label: 'Prior PWS/SOW or template' },
  { role: 'qasp', label: 'QASP / quality data' },
  { role: 'performance_metrics', label: 'Performance metrics' },
  { role: 'staffing', label: 'Staffing / qualification material' },
  { role: 'schedule_transition', label: 'Schedule / transition material' },
  { role: 'government_furnished', label: 'Government-furnished information / property' },
  { role: 'security_records', label: 'Security / records guidance' },
  { role: 'wage_labor', label: 'Wage / labor material' },
  { role: 'cfetp', label: 'CFETP' },
  { role: 'dafman', label: 'DAFMAN' },
  { role: 'other', label: 'Other support document' },
];

type Props = {
  initialView: ServicesPwsChatSessionView;
  onTurn: (input: ServicesPwsChatTurnInput) => Promise<ServicesPwsChatSessionView>;
  onUploadRequirementDocument: (file: File) => Promise<ServicesPwsChatSessionView>;
  onExtractRequirementDocument?: (confirmOverwrite: boolean) => Promise<ServicesPwsChatSessionView>;
  onUploadArtifact: (role: Exclude<ServicesPwsArtifactRole, 'requirements'>, file: File) => Promise<ServicesPwsChatSessionView>;
  onRemoveRequirementDocument?: () => Promise<ServicesPwsChatSessionView>;
  onReplaceArtifact?: (artifactId: string, role: Exclude<ServicesPwsArtifactRole, 'requirements'>, file: File) => Promise<ServicesPwsChatSessionView>;
  onRemoveArtifact?: (artifactId: string) => Promise<ServicesPwsChatSessionView>;
  onPollView?: (poll: number) => Promise<ServicesPwsChatSessionView>;
  onViewGeneratedPws?: () => Promise<void> | void;
  /** Home Chat's owner-scoped cancellation affordance, colocated with busy work. */
  processingAction?: React.ReactNode;
  /** Main Home Chat's launcher-owned utility, never supplied by a companion. */
  headerAction?: React.ReactNode;
};

/** A typed Services façade over the construction-proven chat interaction model. */
const ServicesPwsChatPanel: React.FC<Props> = (props) => {
  // Polling advances the shared panel locally. Preserve the adapter identity
  // across unrelated Home Chat renders so its older action response cannot
  // replace a completed or failed progress response.
  const initialView = useMemo(() => ({
    ...props.initialView,
    // The Services controller exposes its durable job as generation_job;
    // the shared visual contract reads `generation` for polling/spinner UI.
    generation: props.initialView.generation ?? props.initialView.generation_job,
  } as unknown as ConstructionSowChatSessionView), [props.initialView]);
  return <ConstructionSowChatPanel
    initialView={initialView}
    onTurn={props.onTurn as unknown as (input: ConstructionSowChatTurnInput) => Promise<ConstructionSowChatSessionView>}
    onUploadRequirementDocument={props.onUploadRequirementDocument as unknown as (file: File) => Promise<ConstructionSowChatSessionView>}
    onExtractRequirementDocument={props.onExtractRequirementDocument as unknown as ((confirm: boolean) => Promise<ConstructionSowChatSessionView>) | undefined}
    onUploadArtifact={props.onUploadArtifact as unknown as (role: ConstructionSowNewUploadRole, file: File) => Promise<ConstructionSowChatSessionView>}
    onRemoveRequirementDocument={props.onRemoveRequirementDocument as unknown as (() => Promise<ConstructionSowChatSessionView>) | undefined}
    onReplaceArtifact={props.onReplaceArtifact ? ((id, file, role) => props.onReplaceArtifact!(id, role as Exclude<ServicesPwsArtifactRole, 'requirements'>, file) as unknown as Promise<ConstructionSowChatSessionView>) : undefined}
    onRemoveArtifact={props.onRemoveArtifact as unknown as ((id: string) => Promise<ConstructionSowChatSessionView>) | undefined}
    onPollView={props.onPollView as unknown as ((poll: number) => Promise<ConstructionSowChatSessionView>) | undefined}
    onViewGeneratedSow={props.onViewGeneratedPws}
    processingAction={props.processingAction}
    headerAction={props.headerAction}
    presentation={{
      kind: 'services_pws', title: 'Services PWS', requirementLabel: 'Service requirement description',
      requirementDocumentLabel: 'Requirement document', supportRoles: SUPPORT_ROLES,
      projectControlIds: ['spws_project_number', 'spws_project_title'], documentControlsLabel: 'PWS document controls',
      showProjectNumber: false, requireProjectTitle: false,
      generatedDocumentLabel: 'PWS', handoffMessage: 'Your generated PWS is ready to review and edit in the Services workflow.',
      viewGeneratedLabel: 'View generated PWS',
    }}
  />;
};

export default ServicesPwsChatPanel;
